import axios from "axios";
import {
  FormEvent,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FranchiseModel } from "../../../../data/entity/franchise-model";
import { ComponentState } from "../../../../data/enums/ComponentState";
import { pushFormStart, pushGtmEvent } from "../../../../utils/gtm";
import { getHutk } from "../../../../utils/hubspot";
import campaignImage from "../../../assets/lead-form-campaign.webp";
import { FormContext } from "../../../context/contact/contact-context";
import { InputPhone } from "../../input-phone/input-phone";
import { Input } from "../../input/input";
import { Wrapper } from "../wrapper/wrapper";
import * as S from "./lead-form-style";

interface LeadFormValues {
  title: string;
  layout?: "DEFAULT" | "CAMPAIGN";
  backgroundColor?: string;
  campaignImage?: string;
}

interface LeadFormProps {
  id: string;
  values: LeadFormValues;
  franchiseDomain: string;
  params: Record<string, string>;
  franchiseData: FranchiseModel;
}

interface LeadFormData {
  name: string;
  phone: string;
  // cpf: string;
  email: string;
  cep?: string;
}

const validateName = (name: string): string => {
  if (!name.trim()) return "Nome obrigatório";
  if (name.trim().split(/\s+/).filter(Boolean).length < 2)
    return "Informe nome e sobrenome";
  return "";
};

const validatePhone = (phone: string): string => {
  if (!phone) return "Celular obrigatório";
  if (phone.replace(/\D/g, "").length < 10) return "Celular inválido";
  return "";
};

// const validateCpf = (cpf: string): string => {
//   const digits = cpf.replace(/\D/g, "");
//   if (!digits) return "CPF obrigatório";
//   if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits))
//     return "CPF inválido";
//   const toInt = (c: string) => parseInt(c, 10);
//   const sum1 = digits
//     .slice(0, 9)
//     .split("")
//     .reduce((acc, d, i) => acc + toInt(d) * (10 - i), 0);
//   const r1 = (sum1 * 10) % 11;
//   if (toInt(digits[9]) !== (r1 >= 10 ? 0 : r1)) return "CPF inválido";
//   const sum2 = digits
//     .slice(0, 10)
//     .split("")
//     .reduce((acc, d, i) => acc + toInt(d) * (11 - i), 0);
//   const r2 = (sum2 * 10) % 11;
//   if (toInt(digits[10]) !== (r2 >= 10 ? 0 : r2)) return "CPF inválido";
//   return "";
// };

const validateEmail = (email: string): string => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) ? "" : "Informe um e-mail válido";
};

// const maskCpf = (value: string): string => {
//   const digits = value.replace(/\D/g, "").substring(0, 11);
//   return digits
//     .replace(/(\d{3})(\d)/, "$1.$2")
//     .replace(/(\d{3})(\d)/, "$1.$2")
//     .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
// };

const decoupleName = (
  name: string,
): { firstName: string; lastName: string } => {
  const [firstName, ...remaining] = name.split(" ");
  return {
    firstName,
    lastName: remaining.reduce(
      (text, word) => (text ? `${text} ${word}` : word),
      "",
    ),
  };
};

export function LeadForm({
  id,
  values,
  franchiseDomain,
  params,
  franchiseData,
}: LeadFormProps): ReactElement {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [data, setData] = useState<LeadFormData>({
    name: "",
    phone: "",
    // cpf: "",
    email: "",
    cep: "",
  });
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const formStarted = useRef(false);

  /**
   * `form_start` — primeira interação com o formulário. O `focus` do React
   * borbulha, então ouvir no `<form>` cobre todos os campos sem precisar
   * instrumentar cada `Input`.
   */
  const handleFormStart = useCallback(() => {
    if (formStarted.current) return;
    formStarted.current = true;
    pushFormStart();
  }, []);

  const errors = useMemo(() => {
    const result: Record<string, string> = {};

    const name = validateName(data.name);
    if (name) result.name = name;

    const phone = validatePhone(data.phone);
    if (phone) result.phone = phone;

    // const cpf = validateCpf(data.cpf);
    // if (cpf) result.cpf = cpf;

    const email = validateEmail(data.email);
    if (email) result.email = email;

    return result;
  }, [data]);

  const handleSubmit = useCallback(
    (evt: FormEvent) => {
      evt.preventDefault();
      setLoading(true);
      setError(false);
      setSubmitted(true);
      if (Object.keys(errors).length > 0) {
        setLoading(false);
        return;
      }
      void (async () => {
        try {
          const decoupledName = decoupleName(data.name);

          await axios.post("https://api.gt7.com.br/cdtsite/lead/cms", {
            firstname: decoupledName.firstName,
            lastname: decoupledName.lastName,
            phone: data.phone,
            email: data.email,
            // cpf_chave: data.cpf,
            cep: data.cep,
            current_url: franchiseDomain,
            hutk: getHutk(),
            utm_source: params.utm_source,
            utm_campaign: params.utm_campaign,
            utm_medium: params.utm_medium,
            utm_term: params.utm_term,
            utm_content: params.utm_content,
            g_campaignid: params.g_campaignid,
            g_adid: params.g_adid,
            g_network: params.g_network,
            g_device: params.g_device,
            pmax_asset_theme: params.pmax_asset_theme,
            pmax_asset_group: params.pmax_asset_group,
            lp_type: values.layout === "DEFAULT" ? "layout 01" : "layout 02",
            ctn_id: franchiseData.ctnId,
          });

          pushGtmEvent({
            event: "generate_lead",
            firstname: decoupledName.firstName,
            lastName: decoupledName.lastName,
            phone: data.phone,
          });

          const whatsapp = franchiseData.footer.whatsapp;
          navigate(`/cadastro-sucesso${search}`, { state: { whatsapp } });
        } catch {
          setError(true);
        } finally {
          setLoading(false);
        }
      })();
    },
    [
      data,
      errors,
      franchiseDomain,
      navigate,
      franchiseData.ctnId,
      franchiseData.footer.whatsapp,
      params.g_adid,
      params.g_campaignid,
      params.g_device,
      params.g_network,
      params.pmax_asset_group,
      params.pmax_asset_theme,
      params.utm_campaign,
      params.utm_content,
      params.utm_medium,
      params.utm_source,
      params.utm_term,
      search,
      values.layout,
    ],
  );

  const isCampaign = values.layout === "CAMPAIGN";
  // const CpfHint = isCampaign ? S.CampaignCpfHint : S.CpfHint;
  const TitleComponent = isCampaign ? S.CampaignTitle : S.Title;

  const form = (
    <FormContext.Provider
      value={{ errors, submitted, state: ComponentState.READY, content: data }}
    >
      <S.Form onSubmit={handleSubmit} onFocus={handleFormStart}>
        <Input
          label="Nome:"
          id="name"
          placeholder="Digite seu nome"
          value={data.name}
          onChange={(evt) => setData((d) => ({ ...d, name: evt.target.value }))}
          className={isCampaign ? "bg-white" : ""}
        />
        <InputPhone
          label="Celular:"
          id="phone"
          placeholder="(00) 00000-0000"
          value={data.phone}
          onChange={(evt) =>
            setData((d) => ({ ...d, phone: evt.target.value }))
          }
          className={isCampaign ? "bg-white" : ""}
        />
        <Input
          label="E-mail:"
          id="email"
          type="email"
          placeholder="meu@email.com"
          value={data.email}
          onChange={(evt) =>
            setData((d) => ({ ...d, email: evt.target.value }))
          }
          className={isCampaign ? "bg-white" : ""}
        />
        <Input
          label="CEP:"
          id="cep"
          placeholder="00000-000"
          value={data.cep}
          onChange={(evt) => setData((d) => ({ ...d, cep: evt.target.value }))}
          className={isCampaign ? "bg-white" : ""}
        />
        {/* <Input
          label="CPF:"
          id="cpf"
          placeholder="000.000.000-00"
          value={data.cpf}
          onChange={(evt) =>
            setData((d) => ({ ...d, cpf: maskCpf(evt.target.value) }))
          }
          className={isCampaign ? "bg-white" : ""}
        /> */}
        {/* <CpfHint>
          Iremos utilizar apenas para verificar se você já é cliente do cartão
          de todos
        </CpfHint> */}
        <S.Button type="submit" disabled={loading}>
          {loading ? "Enviando..." : "Receber informações"}
        </S.Button>
        {error && (
          <S.ErrorMessage>
            Ocorreu um erro ao enviar. Tente novamente.
          </S.ErrorMessage>
        )}
      </S.Form>
    </FormContext.Provider>
  );

  if (isCampaign) {
    const imageSrc = values.campaignImage || campaignImage;
    return (
      <>
        <span id="formulario" />
        <S.CampaignContainer
          id={id}
          style={
            values.backgroundColor
              ? { backgroundColor: values.backgroundColor }
              : undefined
          }
        >
          <S.CampaignRelativeWrapper>
            <S.CampaignWrapper>
              <S.CampaignFormColumn>
                {values.title && (
                  <TitleComponent
                    dangerouslySetInnerHTML={{ __html: values.title }}
                  />
                )}
                {form}
              </S.CampaignFormColumn>
            </S.CampaignWrapper>
            <S.CampaignImageColumn $src={imageSrc} />
          </S.CampaignRelativeWrapper>
        </S.CampaignContainer>
      </>
    );
  }

  return (
    <>
      <span id="formulario" />
      <S.Container
        id={id}
        style={
          values.backgroundColor
            ? { backgroundColor: values.backgroundColor }
            : undefined
        }
      >
        <Wrapper>
          <S.ContainerWrapper>
            {values.title && (
              <TitleComponent
                dangerouslySetInnerHTML={{ __html: values.title }}
              />
            )}
            {form}
          </S.ContainerWrapper>
        </Wrapper>
      </S.Container>
    </>
  );
}
